import { jsx } from 'react/jsx-runtime';
import { produce } from 'immer';
import { useState, useRef, useCallback, useEffect, useMemo, createContext, useContext } from 'react';
import { useControls } from 'react-zoom-pan-pinch';
import { useRoutePParamChange } from '../../ExpandedResource/hooks/useRoutePParamChange.js';
import { usePDFSearch } from './usePDFSearch.js';

const PDFContentContext = /* @__PURE__ */ createContext({
  pdf: void 0,
  currentPage: 1,
  positionY: 0,
  setCurrentPage: () => {
  },
  updateCurrentPage: () => {
  },
  totalPages: 0,
  isReady: false,
  getEstimatedHeight: () => 0,
  getPagePlaceholderImage: () => null,
  tinyPagesVisible: false,
  scale: 1,
  userScrolled: false,
  setUserScrolled: () => {
  },
  scrollToPage: () => {
  },
  tinyPagesRef: null,
  setTinyPagesRef: () => {
  },
  scrollToTinyPage: () => {
  },
  contentRef: {
    current: null
  },
  textItemPositions: {},
  textItemMatchData: {},
  pagesTextContent: [],
  activeMatch: null,
  matchIndexMapping: {}
});
const usePDFContent = () => useContext(PDFContentContext);
const PDFContentProvider = ({ pdf, children, tinyPagesVisible, userScrolled, setUserScrolled, contentRef, currentPage, scale, setCurrentPage, positionY }) => {
  const [baseViewportWidths, setBaseViewportWidths] = useState([]);
  const [baseHeights, setBaseHeights] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [pagePlaceholderImages, setPagePlaceholderImages] = useState({});
  const pagePlaceholderImageQueue = useRef([]);
  const pagePlaceholderImagesRef = useRef(pagePlaceholderImages);
  const [totalPages, setTotalPages] = useState(0);
  const [tinyPagesRef, setTinyPagesRef] = useState(null);
  const { zoomToElement } = useControls();
  const scrollToTinyPage = useCallback((page) => {
    if (!tinyPagesRef) return;
    const pageElement = tinyPagesRef.querySelector(`[data-page="${page}"]`);
    if (!pageElement) return;
    pageElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center"
    });
  }, [
    tinyPagesRef
  ]);
  const scrollToPage = useCallback((page) => {
    if (!contentRef?.current) return;
    const pageElement = contentRef.current.querySelector(`[data-page-index="${page}"]`);
    if (!pageElement) return;
    zoomToElement(pageElement, void 0, 0);
    scrollToTinyPage(page);
  }, [
    contentRef,
    zoomToElement,
    scrollToTinyPage
  ]);
  const { textItemPositions, textItemMatchData, pagesTextContent, activeMatch, matchIndexMapping } = usePDFSearch({
    pdf,
    currentPage,
    scrollToPage
  });
  useRoutePParamChange({
    onParamChange: (p) => {
      scrollToPage(p);
    },
    enabled: isReady
  });
  const generatingPages = useRef([]);
  const generatePlaceholderImage = useCallback(async (pageNumber) => {
    if (!pdf) return;
    if (generatingPages.current.includes(pageNumber)) return;
    try {
      generatingPages.current.push(pageNumber);
      const page = await pdf.getPage(pageNumber);
      const normalViewport = page.getViewport({
        scale: 2
      });
      const smallViewport = page.getViewport({
        scale: 0.3
      });
      const canvas = document.createElement("canvas");
      canvas.width = normalViewport.width;
      canvas.height = normalViewport.height;
      await page.render({
        canvasContext: canvas.getContext("2d"),
        viewport: normalViewport
      }).promise;
      const normal = canvas.toDataURL("image/jpg", 1);
      canvas.width = smallViewport.width;
      canvas.height = smallViewport.height;
      await page.render({
        canvasContext: canvas.getContext("2d"),
        viewport: smallViewport
      }).promise;
      const small = canvas.toDataURL("image/jpg", 1);
      setPagePlaceholderImages((prev) => {
        const newValue = produce(prev, (draft) => {
          draft[pageNumber] = {
            normal,
            small
          };
        });
        pagePlaceholderImagesRef.current = newValue;
        return newValue;
      });
    } catch (error) {
      console.error("Error", error);
    } finally {
      generatingPages.current = generatingPages.current.filter((item) => item !== pageNumber);
    }
  }, [
    pdf
  ]);
  useEffect(() => {
    if (!pdf) return;
    const calculate = async () => {
      const viewportWidths = [];
      const heights = [];
      for (let i = 0; i < pdf.numPages; i++) {
        try {
          const page = await pdf.getPage(i + 1);
          const viewport = page.getViewport({
            scale: 1
          });
          viewportWidths.push(viewport.width);
          heights.push(viewport.height);
        } catch (error) {
          console.error("Error", error);
          continue;
        }
      }
      setBaseViewportWidths(viewportWidths);
      setBaseHeights(heights);
      setIsReady(true);
    };
    if (pdf.numPages > Object.keys(pagePlaceholderImagesRef.current).length) {
      for (let i = 1; i <= pdf.numPages; i++) {
        if (!pagePlaceholderImagesRef.current[i]) {
          pagePlaceholderImageQueue.current.push(i);
        }
      }
    }
    let workerTimeout;
    let stop = false;
    const processNextImage = async () => {
      const pageNumber = pagePlaceholderImageQueue.current.shift();
      if (pageNumber) {
        await generatePlaceholderImage(pageNumber);
      }
      if (!stop && pagePlaceholderImageQueue.current.length > 0) workerTimeout = window.setTimeout(processNextImage, 1500);
    };
    processNextImage();
    calculate();
    return () => {
      if (workerTimeout) window.clearTimeout(workerTimeout);
      stop = true;
    };
  }, [
    generatePlaceholderImage,
    pdf
  ]);
  const getPagePlaceholderImage = useCallback((pageNumber) => {
    if (!isReady || !pdf) return null;
    const placeholderImage = pagePlaceholderImages[pageNumber];
    if (!placeholderImage) {
      pagePlaceholderImageQueue.current = pagePlaceholderImageQueue.current.filter((item) => item !== pageNumber);
      generatePlaceholderImage(pageNumber);
      return null;
    }
    return placeholderImage;
  }, [
    pagePlaceholderImages,
    generatePlaceholderImage,
    isReady,
    pdf
  ]);
  const getEstimatedHeight = useCallback((pageNumber, width, ignoreScale = false) => {
    if (!pdf || !isReady) return 1;
    const baseWidth = baseViewportWidths[pageNumber - 1] ?? 1 * (ignoreScale ? 1 : scale);
    const baseHeight = baseHeights[pageNumber - 1] ?? 1 * (ignoreScale ? 1 : scale);
    const widthScale = width / baseWidth;
    return baseHeight * widthScale;
  }, [
    pdf,
    baseViewportWidths,
    baseHeights,
    isReady,
    scale
  ]);
  useEffect(() => {
    setTotalPages(pdf?.numPages ?? 0);
  }, [
    pdf,
    setTotalPages
  ]);
  const updateCurrentPage = useCallback(() => {
    if (!contentRef?.current) return;
    const pageElements = contentRef.current.querySelectorAll("[data-page-index]");
    if (!pageElements.length) return;
    let mostVisiblePage = null;
    let highestVisibility = 0;
    pageElements.forEach((element) => {
      const pageNumber = parseInt(element.getAttribute("data-page-index") || "0", 10);
      if (!pageNumber) return;
      const rect = element.getBoundingClientRect();
      const viewportHeight = contentRef.current?.clientHeight ?? 0;
      const viewportWidth = contentRef.current?.clientWidth ?? 0;
      const top = Math.max(0, rect.top);
      const bottom = Math.min(viewportHeight, rect.bottom);
      const left = Math.max(0, rect.left);
      const right = Math.min(viewportWidth, rect.right);
      if (top >= bottom || left >= right) return;
      const visibleHeight = bottom - top;
      const visibleWidth = right - left;
      const visibleArea = visibleHeight * visibleWidth;
      const totalArea = rect.width * rect.height;
      const visibilityPercentage = totalArea > 0 ? visibleArea / totalArea : 0;
      if (visibilityPercentage > highestVisibility) {
        highestVisibility = visibilityPercentage;
        mostVisiblePage = pageNumber;
      }
    });
    if (mostVisiblePage) setCurrentPage((currentPage2) => mostVisiblePage ?? currentPage2);
  }, [
    contentRef,
    setCurrentPage
  ]);
  useEffect(() => {
    updateCurrentPage();
  }, [
    updateCurrentPage,
    positionY
  ]);
  const value = useMemo(() => ({
    pdf,
    currentPage,
    getEstimatedHeight,
    updateCurrentPage,
    isReady,
    getPagePlaceholderImage,
    tinyPagesVisible: tinyPagesVisible ?? false,
    scale,
    userScrolled,
    setUserScrolled,
    scrollToPage,
    totalPages,
    setCurrentPage,
    tinyPagesRef,
    setTinyPagesRef,
    scrollToTinyPage,
    contentRef,
    textItemPositions,
    textItemMatchData,
    pagesTextContent,
    activeMatch,
    matchIndexMapping,
    positionY
  }), [
    pdf,
    currentPage,
    getEstimatedHeight,
    updateCurrentPage,
    isReady,
    getPagePlaceholderImage,
    tinyPagesVisible,
    userScrolled,
    setUserScrolled,
    scale,
    scrollToPage,
    totalPages,
    setCurrentPage,
    tinyPagesRef,
    setTinyPagesRef,
    scrollToTinyPage,
    contentRef,
    textItemPositions,
    textItemMatchData,
    pagesTextContent,
    activeMatch,
    matchIndexMapping,
    positionY
  ]);
  return /* @__PURE__ */ jsx(PDFContentContext.Provider, {
    value,
    children
  });
};

export { PDFContentProvider as default, usePDFContent };
//# sourceMappingURL=PDF.js.map
